import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, Settings } from 'lucide-react';
import { ToastProvider } from './components/Toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import StrategiesPage from './pages/StrategiesPage';
import AgentsPage from './pages/AgentsPage';
import ConnectionsPage from './pages/ConnectionsPage';
import PipelinesPage from './pages/PipelinesPage';
import TradingDashboard from './pages/TradingDashboard';
import ExchangeSettingsPage from './pages/ExchangeSettingsPage';
import LLMProviderPage from './pages/LLMProviderPage';
import CompanySettingsPage from './pages/CompanySettingsPage';
import api from './services/api';

const NAV_LINKS = [
  { to: '/', label: 'Trading' },
  { to: '/agents', label: 'Agents' },
  { to: '/connections', label: 'Connections' },
  { to: '/pipelines', label: 'Pipelines' },
  { to: '/strategies', label: 'Strategies' },
  { to: '/exchange-settings', label: 'Exchanges' },
  { to: '/llm-provider', label: 'LLM Provider' },
];

const NavLink: React.FC<{ to: string; label: string; mobile?: boolean; onClick?: () => void }> = ({
  to, label, mobile, onClick,
}) => {
  const location = useLocation();
  const active = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
  const base = mobile
    ? 'block px-4 py-2 transition rounded-md'
    : 'transition text-sm font-medium px-1 py-0.5 border-b-2';
  const cls = active
    ? mobile
      ? `${base} text-white bg-slate-700`
      : `${base} text-white border-blue-400`
    : mobile
      ? `${base} text-slate-300 hover:text-white hover:bg-slate-700/50`
      : `${base} text-slate-300 hover:text-white border-transparent`;
  return <Link to={to} className={cls} onClick={onClick}>{label}</Link>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? <>{children}</> : <Navigate to="/login" replace />;
};

function AppShell() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [systemStatus, setSystemStatus] = useState('checking');
  const { user, logout, companyName } = useAuth();

  useEffect(() => {
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkHealth = async () => {
    try {
      const res = await api.get('/health');
      setSystemStatus(res.data.status);
    } catch {
      setSystemStatus('offline');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo / Company Name */}
            <Link to="/" className="flex items-center space-x-2 group flex-shrink-0" aria-label="Home">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-lg flex items-center justify-center">
                <span className="text-slate-900 font-bold text-sm">
                  {companyName.slice(0, 2).toUpperCase()}
                </span>
              </div>
              <span className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                {companyName}
              </span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-6">
              {NAV_LINKS.map(l => <NavLink key={l.to} to={l.to} label={l.label} />)}
              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                systemStatus === 'healthy'
                  ? 'bg-green-400/20 text-green-300'
                  : 'bg-red-400/20 text-red-300'
              }`}>
                {systemStatus === 'healthy' ? '● Operational' : '● Offline'}
              </div>
              {/* User + settings + logout */}
              <div className="flex items-center space-x-2 pl-2 border-l border-slate-700">
                <span className="text-slate-400 text-xs">{user?.username}</span>
                <Link
                  to="/company-settings"
                  title="Company Settings"
                  className="text-slate-400 hover:text-blue-400 transition p-1 rounded"
                >
                  <Settings size={15} />
                </Link>
                <button
                  onClick={logout}
                  title="Sign out"
                  className="text-slate-400 hover:text-red-400 transition p-1 rounded"
                >
                  <LogOut size={15} />
                </button>
              </div>
            </div>

            {/* Mobile toggle */}
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-slate-300 hover:text-white">
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden pb-4 pt-2 space-y-1">
              {NAV_LINKS.map(l => (
                <NavLink key={l.to} to={l.to} label={l.label} mobile onClick={() => setIsMenuOpen(false)} />
              ))}
              <NavLink to="/company-settings" label="Company Settings" mobile onClick={() => setIsMenuOpen(false)} />
              <div className="flex items-center justify-between px-4 pt-2 border-t border-slate-700 mt-2">
                <span className="text-slate-400 text-xs">{user?.username}</span>
                <button
                  onClick={logout}
                  className="flex items-center space-x-1 text-slate-400 hover:text-red-400 transition text-xs"
                >
                  <LogOut size={13} />
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route path="/"                   element={<TradingDashboard />} />
          <Route path="/agents"             element={<AgentsPage />} />
          <Route path="/connections"        element={<ConnectionsPage />} />
          <Route path="/pipelines"          element={<PipelinesPage />} />
          <Route path="/strategies"         element={<StrategiesPage />} />
          <Route path="/exchange-settings"  element={<ExchangeSettingsPage />} />
          <Route path="/llm-provider"       element={<LLMProviderPage />} />
          <Route path="/company-settings"   element={<CompanySettingsPage />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-slate-700 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-slate-400 text-sm">
            <p>{companyName} &copy; 2026 — Agentic Hedge Fund Trading System</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <Router>
        <AuthProvider>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />

            {/* Everything else requires auth */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </Router>
    </ToastProvider>
  );
}

export default App;
